import type { GqlClient } from '@/types/gqlClient'
import { graphql } from 'gql.tada'
import { UploadTicketRequest, UploadTicket, FileUploadNotification } from '../types'

/**
 * GraphQL mutation for generateUploadTicket action
 */
const GENERATE_UPLOAD_TICKET_MUTATION = `
  mutation GenerateUploadTicket(
    $projectId: Int
    $urbanAnalysisId: Int
    $filename: String!
    $mimeType: String!
    $fileSize: Int!
    $usage: String
    $section: String
  ) {
    generateUploadTicket(
      projectId: $projectId
      urbanAnalysisId: $urbanAnalysisId
      filename: $filename
      mimeType: $mimeType
      fileSize: $fileSize
      usage: $usage
      section: $section
    ) {
      uploadUrl
      fileKey
      token
      expiresAt
      maxSize
    }
  }
`

/**
 * GraphQL mutation for notifyFileUploaded action
 */
const NOTIFY_FILE_UPLOADED_MUTATION = graphql(`
  mutation NotifyFileUploaded(
    $fileId: String!
    $fileKey: String!
    $token: String!
    $etag: String
    $actualSize: Int
  ) {
    notifyFileUploaded(
      fileId: $fileId
      fileKey: $fileKey
      token: $token
      etag: $etag
      actualSize: $actualSize
    ) {
      fileId
      valid
      processedAt
      archiveJobId
    }
  }
`)

/**
 * Service functions for file upload mutations
 */
export class FileUploadMutations {
  constructor(private graphqlClient: GqlClient) {}

  /**
   * Generates an upload ticket for file upload
   */
  async generateUploadTicket(request: UploadTicketRequest): Promise<UploadTicket> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await (this.graphqlClient as any).request(GENERATE_UPLOAD_TICKET_MUTATION, {
      projectId: request.projectId ?? null,
      urbanAnalysisId: request.urbanAnalysisId ?? null,
      filename: request.filename,
      mimeType: request.mimeType,
      fileSize: request.fileSize,
      usage: request.usage ?? null,
      section: request.section,
    })

    return response.generateUploadTicket
  }

  /**
   * Notifies the backend that file upload is complete. Returns the
   * `archiveJobId` when the backend detected an archive and kicked off
   * extraction — the caller uses that to show a progress UI.
   */
  async notifyFileUploaded(
    notification: FileUploadNotification
  ): Promise<{ archiveJobId: string | null }> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await (this.graphqlClient as any).request(NOTIFY_FILE_UPLOADED_MUTATION, {
      fileId: notification.fileId,
      fileKey: notification.fileKey,
      token: notification.token,
      etag: notification.etag,
      actualSize: notification.actualSize,
    })
    const archiveJobId =
      (response?.notifyFileUploaded?.archiveJobId as string | null | undefined) ?? null
    return { archiveJobId }
  }
}

/**
 * Factory function to create FileUploadMutations instance
 */
export const createFileUploadMutations = (graphqlClient: GqlClient): FileUploadMutations => {
  return new FileUploadMutations(graphqlClient)
}
